<style lang="scss">
.launch-service-container {
    padding: 2rem 18rem 5rem 15rem !important;

    .launch-service-title {
        font-size: 1.875rem;
    }
}

.clickable-element:hover {
    cursor: pointer;
}

.highlight-instance-row {
    border: 2px solid #abc74a !important;
}

.launch-button {
    background: $primary !important;
}

.launch-disabled {
    background: $grayed-out !important;
    color: #fff !important;

    span {
        color: #fff !important;
    }
}
</style>

<template>
    <v-container
        class="ma-0 pa-12"
        fluid
        :data-auto="dataAutoPrefix()">
        <v-row class="wrapper-container">
            <v-col cols="8">
                <div class="d-flex justify-space-between mb-10">
                    <div class="view-headline">
                        <span :data-auto="dataAutoPrefix('-title')">{{ getLocaleFor("launchServiceTitle") }}</span>
                    </div>
                </div>
                <v-row>
                    <v-expansion-panels
                        v-model="panel"
                        class="mb-6"
                        multiple>
                        <TopologySelection
                            :is-topology-selected="isTopologySelected"
                            :parent-data-auto-prefix="dataAutoPrefix"
                            @onRequirementOfDBSelect="onRequirementOfDBSelect"
                            @onTopologySelect="onTopologySelect" />
                        <CloudProviderSelection
                            :get-locale-for="getLocaleFor"
                            :location="location"
                            :provider-type="providerType"
                            :parent-data-auto-prefix="dataAutoPrefix"
                            :current-tier="currentTier"
                            :regions="currentProviderRegions"
                            :topology="topologyName"
                            :is-serverless="isServerlessAnalytics"
                            :is-disabled="shouldDisableCloudProvider"
                            @onCloudProviderSelect="onCloudProviderSelect"
                            @onRegionSelect="onRegionSelect"
                            @on-zone-select="onZoneSelection" />

                        <ConfigurationSelection
                            v-show="!isServerlessAnalytics"
                            :architectures="architectures"
                            :instance-data="filteredInstances"
                            :replicas="replicas"
                            :default-storage-size="selectedInstanceDisk"
                            :parent-data-auto-prefix="dataAutoPrefix"
                            :is-replica-disabled="isReplicaDisabled"
                            :provider-type="providerType"
                            :default-iops-volume-type="iopsVolumeType"
                            :default-iops-volume-size="iopsVolumeSize"
                            :current-tier="currentTier"
                            :topology-range="topologyNodeRange"
                            :show-config-panel="showConfigPanel"
                            :topology="topologyName"
                            :storage-range="storageSizes"
                            :instance-cost="filteredInstanceCosts"
                            :is-disabled="shouldDisableInstanceSelection"
                            :selected-architecture="selectedArchitectureType"
                            :show-replica-widget="shouldHaveReplicaWidget"
                            :selected-instance="selectedInstance"
                            :max-scale-instances="filteredMaxscaleInstances"
                            @onArchitectureSelect="onArchitectureSelect"
                            @onInstanceConfigSelect="onInstanceConfigSelect"
                            @onStorageSelect="onStorageSelect"
                            @onReplicaChange="onReplicaChange"
                            @onVolumeSizeChange="changeIopsVolumeSize"
                            @onVolumeTypeChange="changeIopsVolumeType"
                            @on-auto-txn-storage-toggle="onStorageAutoToggle"
                            @on-auto-node-toggle="onNodeAutoToggle"
                            @on-node-scale-type-selected="onAutoNodeScaleTypeSelection"
                            @on-node-scale-value-selected="onAutoNodeScaleValueSelection"
                            @on-txn-storage-selected="onAutoStorageScaleValueSelection"
                            @on-maxscale-toggle="onMaxScaleToggle"
                            @on-maxscale-node-selection="setMaxScaleNode"
                            @on-maxscale-size-selection="setMaxScaleSize"
                            @on-iops-change="onIopsChange"
                            @on-throughput-change="onThroughputChange" />

                        <ServerSelection
                            :get-locale-for="getLocaleFor"
                            :server-list="serverVersions"
                            :parent-data-auto-prefix="dataAutoPrefix"
                            :should-show-server-list="!isServerlessAnalytics"
                            :default-service-name="serviceName"
                            :is-disabled="shouldDisableServiceAttribute"
                            :region="location"
                            :maintenance-window="defaultMaintenanceWindow"
                            :is-power-tier-user="isPowerTierUser"
                            @onMariaDBServerSelect="onMariaDBServerSelect"
                            @onServiceNameEntered="onServiceNameEntered"
                            @onMaintenanceWindowSelect="onMaintenanceWindowSelect" />

                        <OptionsSelection
                            v-show="additionalOptions.length"
                            :get-locale-for="getLocaleFor"
                            :options="additionalOptions"
                            :is-disabled="shouldDisableOtherAttributes"
                            :parent-data-auto-prefix="dataAutoPrefix"
                            @onOptionToggle="onAdditionalOptionToggle" />

                        <SecuritySelectionV2
                            v-if="$unleash.isFeatureEnabled('enable-portal-dual-endpoints')"
                            :provider-type="providerType"
                            :is-s-sl-enabled="isSSlEnabled"
                            :has-ssl="!isServerlessAnalytics"
                            :is-serverless="isServerlessAnalytics"
                            :is-psc-supported="!isServerlessAnalytics"
                            :is-disabled="shouldDisableSecurity"
                            :parent-data-auto-prefix="dataAutoPrefix"
                            @onSSLToggle="onSSLToggle"
                            @on-service-security-select="handleServiceSecurity"
                            @on-secondary-endpoint-toggled="handleSecondaryEndpointAction"
                            @on-secondary-endpoint-current-ip-select="handleSecondaryEndpointIpSelect" />

                        <SecuritySelection
                            v-else
                            :provider-type="providerType"
                            :is-s-sl-enabled="isSSlEnabled"
                            :has-ssl="!isServerlessAnalytics"
                            :is-serverless="isServerlessAnalytics"
                            :is-psc-supported="!isServerlessAnalytics"
                            :is-disabled="shouldDisableSecurity"
                            :parent-data-auto-prefix="dataAutoPrefix"
                            @onSSLToggle="onSSLToggle"
                            @on-service-security-select="handleServiceSecurity" />
                    </v-expansion-panels>
                </v-row>

                <div class="text-right pt-8">
                    <v-btn
                        :class="['pl-8 pr-8 launch-button text-none', { 'launch-disabled': disableLaunch }]"
                        rounded
                        :dark="!disableLaunch"
                        :disabled="disableLaunch"
                        :data-auto="dataAutoPrefix('-launchService-btn')"
                        @click="shouldPromptForPayment ? openPaymentDialog() : launchService()">
                        <v-icon
                            v-if="!shouldPromptForPayment"
                            size="30"
                            class="mr-3">
                            $vuetify.icons.launchRocket
                        </v-icon>
                        <span>{{
                            shouldPromptForPayment ? getLocaleFor('addPaymentAndLaunch') : getLocaleFor("launchService")
                        }}</span>
                        <v-progress-circular
                            v-if="isServiceCreationInProgress"
                            class="ml-4"
                            :width="5"
                            :size="20"
                            indeterminate />
                    </v-btn>
                </div>
            </v-col>
            <v-col class="ml-6">
                <SelectedServiceOverview
                    :selected-architecture="selectedArchitecture"
                    :selected-instance="selectedInstance"
                    :selected-server="selectedServer"
                    :selected-instance-disk="selectedInstanceDisk"
                    :type-of-product="typeOfProduct"
                    :is-transaction-type="isTransactionType"
                    :topology-type="topologyType"
                    :topology="topologyName"
                    :topology-icon="topologyIcon"
                    :is-topology-selected="isTopologySelected"
                    :provider-type="providerType"
                    :provider-name="providerName"
                    :location="locationLabel"
                    :current-location-icon="currentLocationIcon"
                    :unit-costs="unitCosts"
                    :launch-enabled="!disableLaunch"
                    :parent-data-auto-prefix="dataAutoPrefix"
                    :replicas="replicas"
                    :iops-volume-size="iopsVolumeSize"
                    :iops-volume-type="iopsVolumeType"
                    :service-cost-estimate="serviceCostEstimate"
                    :is-server-less="isServerlessAnalytics"
                    :show-coupon="$unleash.isFeatureEnabled('enable-portal-coupon-widget')" />
            </v-col>
        </v-row>

        <div v-if="showPaymentDialog">
            <PaymentModal
                :payment-profile="paymentProfile"
                :legal-entity-identifier="legalEntityIdentifier"
                :primary-contact-country="billingRecord.primaryContactCountry"
                @on-update-profile="handleProfileUpdate"
                @on-payment-close="handleProfileClose" />
        </div>
    </v-container>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import i18n from 'plugins/i18n'
    import to from 'await-to-js'
    import TopologySelection from 'components/launchService/TopologySelection'
    import CloudProviderSelection from 'components/launchService/CloudProviderSelection'
    import ConfigurationSelection from 'components/launchService/ConfigurationSelection'
    import ServerSelection from 'components/launchService/ServerSelection'
    import OptionsSelection from 'components/launchService/OptionsSelection'
    import SelectedServiceOverview from 'components/launchService/SelectedServiceOverview'
    import SecuritySelection from 'components/launchService/SecuritySelection.vue'
    import SecuritySelectionV2 from 'components/launchService/SecuritySelectionV2.vue'
    import { getFlagIconForRegion } from 'utils/countries'
    import { createService } from 'services/dbServices'
    import { getTopologyOptions } from 'services/offerings'
    import { mapToAutoScalePolicyObjects } from 'mappers/autoScaling'
    import {
        isStandAloneTopology,
        isPrimaryReplica,
        isXpand,
        isColumnstore,
        isServerlessAnalytics,
        isSingleNodeAnalytics,
        getFilteredInstanceSizes,
        isProviderAWS
    } from 'utils/service'
    import { getMultiplierFactor, getMaxScaleRedundancyLabel, getFilteredInstanceCosts, getLegalEntityIdentifier, getPricingForEndpoint, isPaymentSetupPendingForUser } from 'utils/billing'
    import { isPowerTier } from 'utils/organization'
    import PaymentModal from 'components/billing/Payment.vue'

    export default {
        name: 'LaunchService',
        components: {
            TopologySelection,
            CloudProviderSelection,
            ConfigurationSelection,
            ServerSelection,
            OptionsSelection,
            SelectedServiceOverview,
            SecuritySelection,
            SecuritySelectionV2,
            PaymentModal,
        },
        data() {
            return {
                isTopologySelected: false,
                isTransactionType: false,
                typeOfProduct: '',
                typeOfService: '',
                topologyType: '',
                topologyIcon: '',
                providerType: '',
                providerName: '',
                location: null,
                locationLabel: null,
                currentLocationIcon: null,
                // this is std veutify model for keeping track
                // of opened accordion panel states
                // see https://vuetifyjs.com/en/components/expansion-panels/#model
                panel: [0],
                cloudLocationHash: {},
                selectedArchitecture: null,
                selectedArchitectureType: null,
                currentInstanceRowSelected: null,
                disableLaunch: true,
                serviceName: '',
                selectedInstanceDisk: 100,
                selectedServer: null,
                server: null,
                serviceObject: {},
                replicas: 1,
                isReplicaDisabled: true,
                isSSlEnabled: true,
                iopsVolumeType: this.setIopsVolumeType,
                iopsVolumeSize: 0,
                showConfigPanel: false,
                topologyName: '',
                additionalOptions: [],
                iopsRange: [],
                selectedInstance: null,
                nodeAutoScaleOptions: [],
                storageAutoScaleOptions: [],
                isNodeAutoScaleEnabled: false,
                isTxnStorageAutoScaleEnabled: false,
                isServiceCreationInProgress: false,
                whiteListOption: null,
                isMaxScaleRedundancyEnabled: false,
                selectedMaxScaleNode: 2,
                selectedMaxScaleSize: {},
                defaultMaintenanceWindow: '',
                selectedMaintenanceWindow: '',
                filteredInstances: [],
                filteredInstanceCosts: [],
                filteredMaxscaleInstances: [],
                singleZone: '',
                isSecondaryEndpointOpted: false,
                secondaryEndpointIP: null,
                selectedGp3Iops: 0,
                selectedGp3Throughput: 0,
                showPaymentDialog: false,
            }
        },
        computed: {
            ...mapGetters([
                'architectures',
                'instanceSizes',
                'serverVersions',
                'currentProjectId',
                'currentTier',
                'services',
                'storageSizes',
                'nodeSizes',
                'iopsValues',
                'instanceCosts',
                'storageCosts',
                'iopsCosts',
                'regions',
                'maintenanceWindowList',
                'maxScaleInstanceSizes',
                'availabilityZones',
                'volumeTypes',
                'gp3IopsCosts',
                'throughputCosts',
                'endpointPricing',
                'endpointPricingError',
                'billingRecord',
                'paymentProfile',
                'gp3StorageCosts'
            ]),
            localePathForLaunchService() {
                return 'launchService'
            },
            unitCosts() {
                if (!this.selectedInstance) return []
                let storagePricePerHour = this.storageCosts.pricePerHour
                if (this.isGP3Selected) storagePricePerHour = this.gp3StorageCosts.pricePerHour
                let costs = [
                    {
                        name: this.selectedInstance.display_name,
                        value:
                            parseFloat(this.selectedInstance.pricePerHour) * this.costMultiplier,
                    },
                    {
                        name: i18n.t('launchService.ssd-storage', [this.selectedInstanceDisk]),
                        value:
                            parseFloat(storagePricePerHour) *
                            this.selectedInstanceDisk *
                            this.costMultiplier,
                    }
                ]

                // Multi node infra should not be available for SingleNode
                // and SingleNode Analytics as per 1.0
                if (!isStandAloneTopology(this.topologyName) && !this.isSingleNodeAnalytics) {
                    costs.splice(1, 0, {
                        name: i18n.t('launchService.multiNodeInfra'),
                        value:
                            parseFloat(this.selectedInstance.infraPricePerHour) *
                            this.costMultiplier,
                        showToolTip: true,
                        tooltipMessage: this.$t('launchService.scaleFabricInfo'),
                        tooltipReference: 'https://r.mariadb.com/snr-launch-scale-fabric',
                    })
                }

                if (this.isMaxScaleRedundancyEnabled && this.selectedMaxScaleSize) {
                    const { displayName, pricePerHour, } = this.selectedMaxScaleSize
                    costs.splice(1, 0, {
                        name: getMaxScaleRedundancyLabel(this.selectedMaxScaleNode, displayName),
                        value:
                            !isNaN(pricePerHour) ? parseFloat(pricePerHour) * this.selectedMaxScaleNode : 0,
                    })
                }

                if (this.isGP3Selected) {
                    if (this.selectedGp3Iops > this.gp3Iops.total_iops_range_min)
                        costs.push({
                            name: `${this.selectedGp3Iops} ${i18n.t('launchService.provisionedIOPs')}`,
                            value: parseFloat(this.gp3IopsCosts.pricePerHour) * (this.selectedGp3Iops - this.gp3Iops.total_iops_range_min) * this.costMultiplier,
                        })
                    if (this.selectedGp3Throughput > this.gp3Iops.throughput_range_min)
                        costs.push({
                            name: `${this.selectedGp3Throughput} ${i18n.t('mbPerSec')} ${i18n.t('launchService.storageThroughput')}`,
                            // TODO: throughput price check
                            value: parseFloat(this.throughputCosts.pricePerHour) * (this.selectedGp3Throughput - this.gp3Iops.throughput_range_min) * this.costMultiplier,
                        })
                }

                this.isIOPSApplicable &&
                    costs.push({
                        name: i18n.t('launchService.provisionedIOPs'),
                        value:
                            parseFloat(this.iopsCosts.pricePerHour) *
                            this.iopsVolumeSize *
                            this.selectedInstanceDisk *
                            this.costMultiplier,
                    })

                if (this.isSecondaryEndpointOpted) {
                    const endpointPricing = this.endpointPricing.find(priceObj => priceObj.provider === this.providerType)
                    endpointPricing && costs.push({
                        name: i18n.t('dualEndpoints.secondaryEndpoint'),
                        value: getPricingForEndpoint(endpointPricing.prices, 'hour'),
                    })
                }

                return costs
            },
            isServerlessAnalytics() {
                return isServerlessAnalytics(this.topologyName)
            },
            serviceCostEstimate() {
                if (!this.selectedInstance) return {}

                let storagePricePerHour = this.storageCosts.pricePerHour
                if (this.isGP3Selected) storagePricePerHour = this.gp3StorageCosts.pricePerHour
                let storagePricePerMonth = this.storageCosts.pricePerMonth
                if (this.isGP3Selected) storagePricePerMonth = this.gp3StorageCosts.pricePerMonth

                let costPerHour = (parseFloat(this.selectedInstance.pricePerHour) +
                    (parseFloat(storagePricePerHour) * this.selectedInstanceDisk))

                let costPerMonth = (parseFloat(this.selectedInstance.pricePerMonth) +
                    (parseFloat(storagePricePerMonth) * this.selectedInstanceDisk))

                if (!isStandAloneTopology(this.topologyName) && !this.isSingleNodeAnalytics) {
                    costPerHour += parseFloat(this.selectedInstance.infraPricePerHour)
                    costPerMonth += parseFloat(this.selectedInstance.infraPricePerMonth)
                }

                if (this.isIOPSApplicable) {
                    costPerHour += parseFloat(this.iopsCosts.pricePerHour) * this.iopsVolumeSize * this.selectedInstanceDisk
                    costPerMonth += parseFloat(this.iopsCosts.pricePerMonth) * this.iopsVolumeSize * this.selectedInstanceDisk
                }

                if (this.isMaxScaleRedundancyEnabled && this.selectedMaxScaleSize) {
                    costPerHour += !isNaN(this.selectedMaxScaleSize.pricePerHour) ? parseFloat(this.selectedMaxScaleSize.pricePerHour) * this.selectedMaxScaleNode : 0
                    costPerMonth += !isNaN(this.selectedMaxScaleSize.pricePerMonth) ? parseFloat(this.selectedMaxScaleSize.pricePerMonth) * this.selectedMaxScaleNode : 0
                }

                if (this.isGP3Selected) {
                    if (this.selectedGp3Iops > this.gp3Iops.total_iops_range_min) {
                        const iopsSize = this.selectedGp3Iops - this.gp3Iops.total_iops_range_min
                        costPerHour += parseFloat(this.gp3IopsCosts.pricePerHour) * iopsSize
                        costPerMonth += parseFloat(this.gp3IopsCosts.pricePerMonth) * iopsSize
                    }
                    if (this.selectedGp3Throughput > this.gp3Iops.throughput_range_min) {
                        const throughputSize = this.selectedGp3Throughput - this.gp3Iops.throughput_range_min
                        costPerHour += parseFloat(this.throughputCosts.pricePerHour) * throughputSize
                        costPerMonth += parseFloat(this.throughputCosts.pricePerMonth) * throughputSize
                    }
                }

                costPerHour *= this.costMultiplier
                costPerMonth *= this.costMultiplier

                const endpointPricing = this.endpointPricing.find(priceObj => priceObj.provider === this.providerType)

                if (this.isSecondaryEndpointOpted && endpointPricing) {
                    // The secondary endpoint is not dependent on Node
                    // so the costMultiplier do not apply here
                    costPerHour += getPricingForEndpoint(endpointPricing.prices, 'hour')
                    costPerMonth += getPricingForEndpoint(endpointPricing.prices, 'month')
                }

                return {
                    costPerHour,
                    costPerMonth,
                }
            },
            isSingleNodeAnalytics() {
                return isSingleNodeAnalytics(this.topologyName, this.replicas)
            },
            costMultiplier() {
                return getMultiplierFactor(this.topologyName, this.replicas)
            },
            isIOPSApplicable() {
                const { iops: { io1, }, } = this.$config
                return this.isAWSProvider && this.iopsVolumeType === io1
            },
            isAWSProvider() {
                return this.providerType === this.$config.cloudProvider.AWS
            },
            filteredRegionsForServerless() {
                return this.services
                    .filter(({ topology, }) => isStandAloneTopology(topology) || isPrimaryReplica(topology) || isXpand(topology) || isColumnstore(topology))
                    .filter(service => service.status === this.$config.serviceStatus.READY).map(({ region, }) => region)
            },
            shouldDisableCloudProvider() {
                return !this.isTopologySelected
            },
            shouldDisableInstanceSelection() {
                return !this.location
            },
            shouldDisableServiceAttribute() {
                if (this.isServerlessAnalytics) return !this.location
                return !this.selectedInstance
            },
            shouldDisableOtherAttributes() {
                return !this.serviceName
            },
            shouldHaveReplicaWidget() {
                return !isStandAloneTopology(this.topologyName)
            },
            topologyNodeRange() {
                if (isPrimaryReplica(this.topologyName))
                    return this.nodeSizes.map(node => node - 1)

                // As per DBAAS-11987, support only 1 Node for
                // columnstore, this is temporary fix till
                // the backend is able to support multinode
                if (isColumnstore(this.topologyName))
                    return [1]

                return this.nodeSizes
            },
            shouldDisableSecurity() {
                return !this.serviceName
            },
            isPowerTierUser() {
                return isPowerTier(this.currentTier)
            },
            currentProviderRegions() {
                return this.regions.filter(region => region.provider === this.providerType)
            },
            setIopsVolumeType() {
                return this.$unleash.isFeatureEnabled('enable-portal-gp3-storage') ? this.$config.iops.gp3 : this.$config.iops.gp2
            },
            isGP3Selected() {
                return this.iopsVolumeType == this.$config.iops.gp3 && isProviderAWS(this.providerType)
            },
            gp3Iops() {
                let type = this.volumeTypes.find(type => type.name == this.$config.iops.gp3)
                return type ? type.iops[0] : {}
            },
            legalEntityIdentifier() {
                return getLegalEntityIdentifier(this.billingRecord)
            },
            shouldPromptForPayment() {
                if (!this.$unleash.isFeatureEnabled('disable-portal-free-trial')) return false
                const { billingRecord, paymentProfile, } = this
                return isPaymentSetupPendingForUser({ billingRecord, paymentProfile, })
            },
        },
        methods: {
            ...mapActions([
                'getArchitectures',
                'getInstanceSizes',
                'getServerVersions',
                'fetchServices',
                'fetchServiceTypes',
                'fetchProjects',
                'fetchStorageSizes',
                'fetchNodeSizes',
                'fetchIops',
                'fetchInstancePrices',
                'fetchInstancesCost',
                'fetchStorageCost',
                'fetchIOPSCost',
                'createAutoScalePolicies',
                'getRegions',
                'fetchVerticalScaleNodeSizes',
                'getMaxScaleInstanceSizes',
                'fetchMaintenanceWindowList',
                'fetchAvailabilityZones',
                'fetchVolumeTypes',
                'fetchThroughputCost',
                'fetchEndpointPricing',
                'fetchBillingRecord',
                'fetchPaymentProfile',
                'fetchCountries',
                'togglePaymentDialog'
            ]),
            async onRequirementOfDBSelect(dbProductType) {
                this.togglePanelsState(1)
                this.resetNextWidgetsForType('db-select')
                this.typeOfProduct = dbProductType
            },
            async onTopologySelect(topology) {
                this.togglePanelsState(1)
                this.resetNextWidgetsForType('topology-select')
                this.showConfigPanel = false
                const { name, } = topology
                this.topologyName = name
                this.topologyType = this.$i18n.t(`topology.${name}`)
                this.typeOfService = topology.service_type
                this.isTransactionType = topology.service_type === this.$config.TRANSACTIONAL

                const { cloudProvider: { AWS, }, } = this.$config
                this.providerType = AWS
                this.location = ''
                this.locationLabel = ''
                this.currentLocationIcon = ''

                this.$lodash.isEmpty(this.regions) && this.getRegions()
                this.isReplicaDisabled = topology.display_name.toLowerCase().includes('single')
                await this.checkNoSQLSupport(topology.name)

                if (this.$unleash.isFeatureEnabled('enable-portal-auto-tune')) {
                    const autoTuneOptionName = 'auto-tune'
                    this.isTransactionType ? this.addAdditionalOption({
                        name: autoTuneOptionName,
                        label: 'enableAutoTuning',
                        hint: 'autoTuneDescription',
                    }) : this.removeAdditionalOption(autoTuneOptionName)
                }

                this.topologyIcon = topology.icon
                this.isTopologySelected = true
                this.togglePanelsState(2)
                this.fetchStorageSizes(name)
                this.setNodeSizesForTopology(name)
                this.fetchVolumeTypes(this.providerType)
                if (this.$unleash.isFeatureEnabled('enable-portal-single-zone') && this.$lodash.isEmpty(this.availabilityZones)) this.fetchAvailabilityZones()

                this.scrollToElement('cloudProviderPanel')
            },
            onCloudProviderSelect(type) {
                this.togglePanelsState(2)
                this.resetNextWidgetsForType('cloud-provider-select')
                this.providerType = type

                if (type === this.$config.cloudProvider.AWS) {
                    this.providerName = 'AWS'
                    this.changeIopsVolumeType(this.iopsVolumeType)
                } else {
                    this.providerName = 'Google Cloud'
                    this.deleteFromServiceObject('volume_iops')
                    this.deleteFromServiceObject('volume_type')
                }
                this.fetchVolumeTypes(type)
            },
            async onRegionSelect(region) {
                this.resetNextWidgetsForType('region-select')
                this.showConfigPanel = false
                this.location = region.name
                this.defaultMaintenanceWindow = region.maintenanceWindow
                this.locationLabel = region.label
                this.currentLocationIcon = `$verify.icons.${getFlagIconForRegion(region.name)}`

                if (this.isServerlessAnalytics) {
                    this.fetchServices()
                    await this.fetchMaintenanceWindowList()
                    this.scrollToElement('serverPanel')
                    this.togglePanelsState(4)
                    return
                }

                this.scrollToElement('configurationPanel')
                this.togglePanelsState(2)
                await Promise.all([
                    this.getArchitectures({
                        topology: this.topologyName,
                        provider: this.providerType,
                    }),
                    this.$lodash.isEmpty(this.instanceSizes) && this.getInstanceSizes(),
                    this.$lodash.isEmpty(this.instanceCosts) && this.fetchInstancesCost({}),
                    // Storage costs can vary if Cloud provider has changed hence keeping this call
                    this.fetchStorageCost({
                        topology: this.topologyName,
                        provider: this.providerType,
                    }),
                    this.isAWSProvider && this.$lodash.isEmpty(this.iopsCosts) &&
                        this.fetchIOPSCost({
                            topology: this.topologyName,
                            provider: this.providerType,
                        }),
                    this.isAWSProvider && this.$lodash.isEmpty(this.throughputCosts) &&
                        this.fetchThroughputCost({
                            topology: this.topologyName,
                            provider: this.providerType,
                        }),
                    this.isAWSProvider && this.$lodash.isEmpty(this.iopsValues) && this.fetchIops(),
                    this.isPowerTierUser && this.$lodash.isEmpty(this.maxScaleInstanceSizes) && this.fetchMaxScaleSizes()
                ])

                this.togglePanelsState(3)

                this.selectedArchitectureType = this.$config.DEFAULT_ARCHITECTURE
                this.selectedArchitecture = this.architectures.find(
                    (arch) => arch.name === this.$config.DEFAULT_ARCHITECTURE
                ).displayName

                this.filterInstances()
                this.filterInstanceCosts()
                this.filterMaxScaleInstances()

                if (this.isAWSProvider) {
                    this.setDefaultIops()
                }

                this.scrollToElement('configurationPanel')
                this.showConfigPanel = true
            },
            onArchitectureSelect({ name, displayName, }) {
                this.resetNextWidgetsForType('arch-select')
                this.togglePanelsState(3)
                this.selectedArchitecture = displayName
                this.selectedArchitectureType = name
                this.filterInstances()
                this.filterInstanceCosts()
                this.filterMaxScaleInstances()
            },
            async onInstanceConfigSelect(instance) {
                this.selectedInstance = instance

                const { TRANSACTIONAL, ANALYTICAL, } = this.$config

                await Promise.all([
                    this.fetchVerticalScaleNodeSizes({
                        // eslint-disable-next-line camelcase
                        service_type: this.isTransactionType ? TRANSACTIONAL : ANALYTICAL,
                        provider: this.providerType,
                        ...(this.isAWSProvider && { architecture: this.serviceObject.architecture, }),
                        // eslint-disable-next-line camelcase
                        min_node_size: this.selectedInstance.name,
                    }),
                    this.$lodash.isEmpty(this.maintenanceWindowList) && this.fetchMaintenanceWindowList()
                ])

                this.togglePanelsState(4)
                this.getServerVersions(this.topologyName)
                this.scrollToElement('serverPanel')
            },
            onStorageSelect(size) {
                this.selectedInstanceDisk = size
            },
            onReplicaChange(count) {
                this.replicas = isPrimaryReplica(this.topologyName) ? ++count : count
            },
            onMariaDBServerSelect(server) {
                if (!server) return
                this.server = server
                this.selectedServer = server.display_name
            },
            onServiceNameEntered(serviceNameObj) {
                this.disableLaunch = serviceNameObj.hasErrors
                this.serviceName = serviceNameObj.name
                if (!serviceNameObj.hasErrors) {
                    this.togglePanelsState(6)
                }
            },
            getLocaleFor(key) {
                return i18n.t(`${this.localePathForLaunchService}.${key}`)
            },
            scrollToElement(elementHash) {
                setTimeout(() => {
                    const el = document.getElementById(elementHash)

                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', })
                    }
                // Time out given for delaying till elements render
                }, 400)
            },
            dataAutoPrefix(string) {
                const preFix = 'launchService'

                // Append string behind the main view to create a
                // data-auto tag with proper value to identify the
                // element uniquely
                if (string) {
                    return `${preFix}${string}`
                }
                return preFix
            },
            async launchService() {
                this.isServiceCreationInProgress = true
                this.finalizeServiceObjectForLaunch()
                // Make a post and redirect on success
                const [error, response] = await to(createService(this.serviceObject))

                if (error) {
                    // TODO:: Need to work on showing the error UX
                    this.$store.commit('showMessage', { text: error.response.data.errors[0].message, type: 'error', })
                } else {
                    this.fetchServices()
                    const actions = this.setupAutoScaling()
                    if (actions.length) {
                        // eslint-disable-next-line camelcase
                        let scalingPolicies = { service_name: response.name, service_id: response.id, actions, }
                        await this.createAutoScalePolicies(scalingPolicies)
                    }

                    this.$router.push({ name: 'dashboard', })
                }
                this.isServiceCreationInProgress = false
            },

            finalizeServiceObjectForLaunch() {

                this.appendToServiceObject('project_id', this.currentProjectId)
                this.appendToServiceObject('service_type', this.typeOfService)
                this.appendToServiceObject('topology', this.topologyName)
                this.appendToServiceObject('provider', this.providerType)
                this.appendToServiceObject('region', this.location)
                this.appendToServiceObject('name', this.serviceName)
                if (this.whiteListOption) this.setupServiceObjectForWhiteListing()
                // Dual Endpoint Setup
                if (this.$unleash.isFeatureEnabled('enable-portal-dual-endpoints') && this.isSecondaryEndpointOpted) this.setupServiceObjectForSecondaryEndpoint()

                if (this.isPowerTierUser) {
                    this.appendToServiceObject('maintenance_window', this.selectedMaintenanceWindow ? this.selectedMaintenanceWindow : this.maintenanceWindowList[0].name)
                }

                if (!this.isServerlessAnalytics) {
                    // Architecture is supported only for AWS provider
                    this.isAWSProvider && this.appendToServiceObject('architecture', this.selectedArchitectureType || this.$config.DEFAULT_ARCHITECTURE)
                    this.appendToServiceObject('nodes', this.replicas)
                    this.appendToServiceObject('size', this.selectedInstance.name)
                    this.appendToServiceObject('storage', this.selectedInstanceDisk)
                    this.appendToServiceObject('ssl_enabled', this.isSSlEnabled)
                    this.appendToServiceObject('version', this.server.name)
                    // Don't send volume_type for GCP
                    this.isAWSProvider && this.appendToServiceObject('volume_type', this.iopsVolumeType)
                } else {
                    // Serverless don't require nodes
                    this.deleteFromServiceObject('nodes')
                }
                if (this.iopsVolumeType === this.$config.iops.io1) {
                    this.deleteFromServiceObject('volume_iops')
                    this.appendToServiceObject('volume_iops', this.iopsVolumeSize * this.selectedInstanceDisk)
                }

                if (this.isGP3Selected) {
                    this.appendToServiceObject('volume_iops', this.selectedGp3Iops)
                    this.appendToServiceObject('volume_throughput', this.selectedGp3Throughput)
                }

                if (this.additionalOptions.length) {
                    this.additionalOptions.forEach((option) =>
                        this.appendToServiceObject(option.name, option.value)
                    )
                }
                if (this.isMaxScaleRedundancyEnabled && this.setMaxScaleSize) {
                    this.appendToServiceObject('maxscale_nodes', this.selectedMaxScaleNode)
                    this.appendToServiceObject('maxscale_size', this.selectedMaxScaleSize.name)
                }

                if (this.$unleash.isFeatureEnabled('enable-portal-single-zone') && this.singleZone) this.appendToServiceObject('availability_zone', this.singleZone)
            },

            setupServiceObjectForWhiteListing() {
                const { name, value, } = this.whiteListOption[0]
                if (name === 'ip-whitelist') {
                    if (value) this.appendToServiceObject('allow_list', [{ 'ip': value, }])
                    this.deleteFromServiceObject('endpoint_mechanism')
                    this.deleteFromServiceObject('endpoint_allowed_accounts')
                } else {
                    // private link/psc setup
                    this.appendToServiceObject('endpoint_mechanism', this.$config.ENDPOINT_MECHANISM_PRIVATELINK)
                    // Empty array as launch has no option to select project id
                    this.appendToServiceObject('endpoint_allowed_accounts', [])
                }
            },

            appendToServiceObject(key, value) {
                this.serviceObject[key] = value
            },

            deleteFromServiceObject(key) {
                delete this.serviceObject[key]
            },

            onSSLToggle(value) {
                this.isSSlEnabled = !value
            },

            changeIopsVolumeSize(size) {
                // Handle iops volume size change
                this.iopsVolumeSize = size
            },

            changeIopsVolumeType(type) {
                this.iopsVolumeType = type
            },
            togglePanelsState(panelsToOpen) {
                this.panel = [...Array(panelsToOpen).keys()]
            },
            async checkNoSQLSupport(topologyName) {
                const optionName = 'nosql_enabled'
                const [error, response] = await to(getTopologyOptions(topologyName))

                if (error || !response || !response.nosql_available) return this.removeAdditionalOption(optionName)

                this.addAdditionalOption({
                    name: optionName,
                    label: 'enableNoSQL',
                    hint: 'noSQLInfo',
                })
            },
            addAdditionalOption({ name, label, hint, value = false, }) {
                if (this.additionalOptions.findIndex(option => option.name === name) !== -1) return
                this.additionalOptions.push({ name, label, hint, value, })
            },
            removeAdditionalOption(optionName) {
                const optionIndex = this.additionalOptions.findIndex(option => option.name === optionName)
                if (optionIndex !== -1) this.additionalOptions.splice(optionIndex, 1)
            },
            updateAdditionalOption(optionName, value) {
                let additionalOption = this.additionalOptions.find(options => options.name === optionName)
                if (additionalOption) {
                    additionalOption.value = value
                }
            },
            onAdditionalOptionToggle({ optionName, value, }) {
                this.updateAdditionalOption(optionName, value)
            },
            async setNodeSizesForTopology(topology) {
                await this.fetchNodeSizes(topology)
                // Defaulting the nodes to second value coming
                // from API
                // In case of analytics only single value is supported
                if (this.nodeSizes && this.nodeSizes.length) {
                    const defaultReplicaSize = this.getDefaultNodeSizeForTopology(this.topologyName, this.nodeSizes)
                    this.onReplicaChange(defaultReplicaSize)
                }
            },
            onAutoNodeScaleTypeSelection(scaleType) {
                // We support single scale option, so pop out existing
                if (this.nodeAutoScaleOptions.length) this.nodeAutoScaleOptions.pop()
                this.nodeAutoScaleOptions.push({ type: scaleType, })
            },
            onAutoNodeScaleValueSelection(scaleValue) {
                this.nodeAutoScaleOptions[0].maxValue = scaleValue
            },
            onAutoStorageScaleValueSelection(storageValue) {
                // We support single scale option, so pop out existing
                if (this.storageAutoScaleOptions.length) this.storageAutoScaleOptions.pop()
                this.storageAutoScaleOptions.push({
                    type: this.$config.AUTO_STORAGE_SCALE,
                    value: storageValue,
                })
            },
            onStorageAutoToggle(isChecked) {
                this.isTxnStorageAutoScaleEnabled = isChecked
                if (!isChecked) this.storageAutoScaleOptions.pop()
            },
            onNodeAutoToggle(isChecked) {
                this.isNodeAutoScaleEnabled = isChecked
                if (!isChecked) this.nodeAutoScaleOptions.pop()
            },
            setupAutoScaling() {
                // Is Node Scaling enabled
                let autoScalePolicies = []
                if (this.isNodeAutoScaleEnabled && this.nodeAutoScaleOptions.length) {
                    if (this.nodeAutoScaleOptions[0].type === this.$config.AUTO_VERTICAL_SCALE) {
                        this.nodeAutoScaleOptions[0].minValue = this.serviceObject.size
                    } else {
                        this.nodeAutoScaleOptions[0].minValue = this.serviceObject.nodes
                    }
                    autoScalePolicies.push(this.nodeAutoScaleOptions.map(mapToAutoScalePolicyObjects))
                }

                // Is storage Scaling enabled
                if (this.isTxnStorageAutoScaleEnabled && this.storageAutoScaleOptions.length) {
                    autoScalePolicies.push(this.storageAutoScaleOptions.map(mapToAutoScalePolicyObjects))
                }
                return autoScalePolicies.flat()
            },
            resetNextWidgetsForType(currentWidget) {
                switch (currentWidget) {
                    case 'db-select':
                        this.resetTopologyType()
                        this.resetAdditionalOptions()
                        this.resetNextWidgetsForType('topology-select')
                        break
                    case 'topology-select':
                        this.resetCloudProviderStep()
                        this.resetNextWidgetsForType('cloud-provider-select')
                        break
                    case 'cloud-provider-select':
                        this.resetRegionSelectionStep()
                        this.resetNextWidgetsForType('region-select')
                        break
                    case 'region-select':
                        this.resetArchType()
                        this.resetNextWidgetsForType('arch-select')
                        break
                    case 'arch-select':
                        if (!this.isServerlessAnalytics) {
                            this.resetStorageInstanceStep()
                        }
                        this.resetServiceAttributeStep()
                        this.resetSecurityOptions()
                        break
                }
            },
            resetTopologyType() {
                this.topologyName = ''
                this.topologyType = ''
                this.topologyIcon = ''
                this.isTopologySelected = false
            },
            resetCloudProviderStep() {
                this.providerType = ''
                this.providerName = ''
            },
            resetRegionSelectionStep() {
                this.location = null
                this.locationLabel = ''
                this.currentLocationIcon = ''
                this.showConfigPanel = false
            },
            resetArchType() {
                this.singleZone = ''
                this.selectedArchitecture = null
                this.selectedArchitectureType = this.$config.DEFAULT_ARCHITECTURE
            },
            resetStorageInstanceStep() {
                this.selectedInstance = null
                this.selectedInstanceDisk = 100
                if (this.topologyName && this.nodeSizes) {
                    this.replicas = this.getDefaultNodeSizeForTopology(this.topologyName, this.nodeSizes)
                }
                this.iopsVolumeType = this.setIopsVolumeType
                this.setDefaultIops()
            },
            resetServiceAttributeStep() {
                this.serviceName = ''
                this.selectedServer = null
                this.isSSlEnabled = true
                this.disableLaunch = true
            },
            resetAdditionalOptions() {
                this.additionalOptions = []
            },
            resetSecurityOptions() {
                this.isSSlEnabled = true
                this.whiteListOption = null
            },
            // Get default node size for topology
            // to initialize the launch flow
            getDefaultNodeSizeForTopology(topology, nodeSizes) {
                if (isPrimaryReplica(topology)) return nodeSizes[2]
                return nodeSizes[0]
            },
            handleServiceSecurity(val) {
                this.whiteListOption = val
            },
            onMaxScaleToggle(isChecked) {
                this.isMaxScaleRedundancyEnabled = isChecked
            },
            fetchMaxScaleSizes() {
                this.getMaxScaleInstanceSizes({
                    type: this.$config.INSTANCE_SIZE_TYPE.PROXY,
                })
            },
            setMaxScaleNode(val) {
                this.selectedMaxScaleNode = val
            },
            setMaxScaleSize(val) {
                this.selectedMaxScaleSize = val
            },
            onMaintenanceWindowSelect(val) {
                this.selectedMaintenanceWindow = val
            },
            filterInstances() {
                // Filter instances for the current user selection
                // from list of all the sizes fetched from API
                this.filteredInstances = getFilteredInstanceSizes(this.instanceSizes, {
                    provider: this.providerType,
                    topology: this.topologyName,
                    arch: this.selectedArchitectureType,
                })
            },
            filterInstanceCosts() {
                const { provider, topology, } = this.filteredInstances[0]
                this.filteredInstanceCosts = getFilteredInstanceCosts(this.instanceCosts, {
                    provider,
                    topology,
                    arch: this.selectedArchitectureType,
                })
            },
            filterMaxScaleInstances() {
                this.filteredMaxscaleInstances = getFilteredInstanceSizes(this.maxScaleInstanceSizes, {
                    provider: this.providerType,
                    topology: this.topologyName,
                    arch: this.selectedArchitectureType,
                })
            },
            setDefaultIops() {
                this.changeIopsVolumeSize(this.$typy(this, 'iopsValues[0].default_iops_per_gb').safeNumber || this.$config.IOPS_DEFAULT)
            },
            onZoneSelection(zone) {
                this.singleZone = zone
            },
            handleSecondaryEndpointAction(isSecondaryEndpointOpted) {
                this.isSecondaryEndpointOpted = isSecondaryEndpointOpted
            },
            handleSecondaryEndpointIpSelect(selectedIP) {
                this.secondaryEndpointIP = selectedIP
            },
            setupServiceObjectForSecondaryEndpoint() {
                // Backend limitation to have max 16 length
                // and regex to match ^[a-z][a-z0-9-]+$
                // so generating a random string using only
                // lowercase alphabets
                const name = this.$help.getUniqueString(16)
                this.appendToServiceObject('endpoints', [{ name, }])
                // In case a Secondary endpoint is selected with current IP
                // set the allow_list considering the IP selected
                // as it is shared IP
                // Overriding the existing allow_list will not change anything
                if (this.secondaryEndpointIP) {
                    this.appendToServiceObject('allow_list', [{ 'ip': this.secondaryEndpointIP, }])
                }
            },
            onIopsChange(val) {
                this.selectedGp3Iops = val
            },
            onThroughputChange(val) {
                this.selectedGp3Throughput = val
            },
            async openPaymentDialog() {
                if (this.$lodash.isEmpty(this.paymentProfile)) {
                    await this.fetchPaymentProfile()
                }
                this.showPaymentDialog = true
                if (this.$lodash.isEmpty(this.billingCountries)) {
                    await this.fetchCountries()
                } else {
                    // Minor delay is required to open the
                    // payment dialog for cached data
                    await this.$nextTick()
                }
                this.togglePaymentDialog(true)
            },
            handleProfileUpdate() {
                this.togglePaymentDialog(false)
                this.showPaymentDialog = false
                // On success perform the launch action
                this.launchService()
            },
            handleProfileClose() {
                this.togglePaymentDialog(false)
                this.showPaymentDialog = false
            },
        },
        created() {
            if (this.$lodash.isEmpty(this.currentProjectId)) this.fetchProjects()
            if (this.$lodash.isEmpty(this.billingRecord)) this.fetchBillingRecord()
            if (this.$lodash.isEmpty(this.paymentProfile)) this.fetchPaymentProfile()
        },
        mounted() {
            // fetch and cache secondary endpoint charges
            // if it is not already available
            if (!this.endpointPricing.length) {
                this.fetchEndpointPricing()
            }
        },
    }
</script>
